import React, { useState, useEffect, useCallback } from 'react';
import { ref, get as dbGet, set as dbSet, runTransaction } from 'firebase/database';
import { dbRealtime } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { encodeEmail, classifyDescription } from '../utils/helpers';
import { getMappingsRaw, invalidateCache } from '../utils/mappingCache';

function Classifier() {
  const [inputData, setInputData] = useState('');
  const [outputData, setOutputData] = useState([]);
  const [message, setMessage] = useState('');
  const [glAccountMapping, setGlAccountMapping] = useState({});
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [userUsage, setUserUsage] = useState({ today: 0, total: 0 });
  const { user } = useAuth();

  const trackUsage = useCallback(async (descriptionCount) => {
    if (!user?.email) return;

    try {
      const today = new Date().toISOString().split('T')[0];

      // Atomic counter updates using runTransaction
      const refs = [
        ref(dbRealtime, `usage/daily/${today}`),
        ref(dbRealtime, 'usage/total'),
        ref(dbRealtime, `usage/users/${encodeEmail(user.email)}/daily/${today}`),
        ref(dbRealtime, `usage/users/${encodeEmail(user.email)}/total`),
      ];

      await Promise.all(
        refs.map(r =>
          runTransaction(r, (current) => (current || 0) + descriptionCount)
        )
      );

      setUserUsage(prev => ({
        today: prev.today + descriptionCount,
        total: prev.total + descriptionCount,
      }));
    } catch (error) {
      console.error('Error tracking usage:', error);
    }
  }, [user?.email]);

  const fetchUserUsage = useCallback(async () => {
    if (!user?.email) return;

    try {
      const today = new Date().toISOString().split('T')[0];
      const userDailyRef = ref(dbRealtime, `usage/users/${encodeEmail(user.email)}/daily/${today}`);
      const userTotalRef = ref(dbRealtime, `usage/users/${encodeEmail(user.email)}/total`);

      const [userDailySnapshot, userTotalSnapshot] = await Promise.all([
        dbGet(userDailyRef),
        dbGet(userTotalRef),
      ]);

      setUserUsage({
        today: userDailySnapshot.exists() ? userDailySnapshot.val() : 0,
        total: userTotalSnapshot.exists() ? userTotalSnapshot.val() : 0,
      });
    } catch (error) {
      console.error('Error fetching user usage:', error);
    }
  }, [user?.email]);

  const fetchMappings = useCallback(async () => {
    try {
      const data = await getMappingsRaw();
      const mappings = {};
      Object.keys(data).forEach((key) => {
        if (data[key]) {
          if (typeof data[key] === 'string') {
            mappings[key.toLowerCase()] = data[key];
          } else if (data[key].description && data[key].glAccount) {
            mappings[data[key].description.toLowerCase()] = data[key].glAccount;
          }
        }
      });
      setGlAccountMapping(mappings);
      return mappings;
    } catch (error) {
      console.error("Error fetching GL mappings:", error);
      setMessage('Failed to load custom mappings.');
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchMappings();
      fetchUserUsage();
    }
  }, [user, fetchMappings, fetchUserUsage]);

  const processInput = useCallback(async () => {
    if (!inputData.trim()) {
      setOutputData([]);
      setProgress(0);
      setIsProcessing(false);
      return;
    }
    setIsProcessing(true);
    setProgress(0);
    const lines = inputData.split('\n');
    const batchSize = 200;
    let results = [];
    for (let i = 0; i < lines.length; i += batchSize) {
      const batch = lines.slice(i, i + batchSize);
      const batchResults = batch.map((line) => {
        const glAccount = line.trim() ? classifyDescription(line, glAccountMapping) : '';
        return {
          description: line,
          glAccount: glAccount,
          newlyCalculatedAccount: null,
          needsVerification: false,
        };
      });
      results = results.concat(batchResults);
      setOutputData([...results]);
      setProgress(Math.min(100, Math.round(((i + batch.length) / lines.length) * 100)));
      if (i + batchSize < lines.length) {
        await new Promise(res => setTimeout(res, 10));
      }
    }
    setProgress(100);
    setIsProcessing(false);

    const validDescriptions = lines.filter(line => line.trim()).length;
    if (validDescriptions > 0) {
      await trackUsage(validDescriptions);
    }
  }, [inputData, glAccountMapping, trackUsage]);

  const reclassifyAndCheckConflicts = useCallback((newMappings) => {
    const reclassifiedData = outputData.map(item => {
      if (!item.description.trim()) return item;

      const newGlAccount = classifyDescription(item.description, newMappings);

      if (item.glAccount && item.glAccount !== newGlAccount) {
        return { ...item, newlyCalculatedAccount: newGlAccount, needsVerification: true };
      }

      return { ...item, glAccount: newGlAccount, needsVerification: false, newlyCalculatedAccount: null };
    });
    setOutputData(reclassifiedData);
  }, [outputData]);

  const handleRefreshAndReclassify = async () => {
    setIsRefreshing(true);
    setMessage('Refreshing mappings and re-classifying...');
    await invalidateCache();
    const newMappings = await fetchMappings();
    if (newMappings) {
      reclassifyAndCheckConflicts(newMappings);
    }
    setMessage('Re-classification complete. Please review suggestions.');
    setIsRefreshing(false);
    setTimeout(() => setMessage(''), 4000);
  };

  const handleVerification = async (index, useNew) => {
    const newOutputData = [...outputData];
    const item = newOutputData[index];

    if (useNew) {
      const originalNewAccount = item.newlyCalculatedAccount;
      const cleanedAccount = originalNewAccount.replace(/\(Personal\)/ig, '').trim();
      item.glAccount = cleanedAccount;
      try {
        await dbSet(ref(dbRealtime, `gl_mappings/${item.description.toLowerCase()}`), cleanedAccount);
        await invalidateCache();
        setMessage('Mapping updated with your choice.');
        setGlAccountMapping(prev => ({ ...prev, [item.description.toLowerCase()]: cleanedAccount }));
      } catch (error) {
        console.error("Error saving user's choice:", error);
        setMessage("Failed to save your choice.");
      } finally {
        setTimeout(() => setMessage(''), 3000);
      }
    }
    item.needsVerification = false;
    item.newlyCalculatedAccount = null;
    setOutputData(newOutputData);
  };

  const handleDescriptionChange = (index, newDescription) => {
    const newOutputData = [...outputData];
    const newGlAccount = classifyDescription(newDescription, glAccountMapping);
    newOutputData[index] = {
      description: newDescription,
      glAccount: newGlAccount,
      newlyCalculatedAccount: null,
      needsVerification: false,
    };
    setOutputData(newOutputData);
  };

  const copyDescAndGL = () => {
    if (outputData.length === 0) return;
    const text = outputData.map(item => `${item.description}\t${item.glAccount}`).join('\n');
    navigator.clipboard.writeText(text).then(() => {
      setMessage(`Copied ${outputData.length} rows (description + GL account)!`);
      setTimeout(() => setMessage(''), 3000);
    }).catch(() => {
      setMessage('Failed to copy data.');
      setTimeout(() => setMessage(''), 3000);
    });
  };

  const copyGLOnly = () => {
    if (outputData.length === 0) return;
    const text = outputData.map(item => item.glAccount).join('\n');
    navigator.clipboard.writeText(text).then(() => {
      setMessage(`Copied ${outputData.length} GL accounts!`);
      setTimeout(() => setMessage(''), 3000);
    }).catch(() => {
      setMessage('Failed to copy data.');
      setTimeout(() => setMessage(''), 3000);
    });
  };

  const handleClear = () => {
    setInputData('');
    setOutputData([]);
  };

  return (
    <div className="container">
      <div className="page-header">
        <h2>Classifier</h2>
        <p>Paste descriptions to automatically classify GL accounts</p>
      </div>

      {/* User Usage Statistics */}
      <div className="stats-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
            <div className="usage-stat">
              <div className="usage-stat__value">{userUsage.today}</div>
              <div className="usage-stat__label">Today</div>
            </div>
            <div className="usage-stat">
              <div className="usage-stat__value">{userUsage.total}</div>
              <div className="usage-stat__label">Total</div>
            </div>
          </div>
          <div style={{ fontSize: '0.9rem', opacity: 0.9 }}>
            Your description count
          </div>
        </div>
      </div>

      {/* Input Card */}
      <div className="card" style={{ marginBottom: '2rem' }}>
        <h3 style={{ color: 'var(--color-primary)', fontWeight: 600, marginBottom: '1rem' }}>Input Descriptions</h3>
        <textarea
          value={inputData}
          onChange={(e) => setInputData(e.target.value)}
          placeholder={"Paste multiple descriptions here (one per line)...\nOffice supplies\nRent payment\nUtility bill\nEmployee salary"}
          className="form-control"
          style={{
            fontFamily: 'monospace',
            fontSize: '14px',
            minHeight: '100px',
            resize: 'vertical'
          }}
        />
        <div style={{ marginTop: '0.75rem', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
          <button
            onClick={processInput}
            disabled={!inputData.trim() || isProcessing}
            className="btn btn-primary"
          >
            {isProcessing ? 'Classifying...' : 'Classify'}
          </button>
          {inputData.trim() && (
            <button onClick={handleClear} className="btn btn-secondary">
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      {message && (
        <div className="alert alert-success" style={{ marginBottom: '1rem' }}>
          {message}
        </div>
      )}

      {/* Results Card */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
          <h3 style={{ color: 'var(--color-primary)', fontWeight: 600 }}>
            Results {outputData.length > 0 && `(${outputData.length})`}
          </h3>
          {outputData.length > 0 && (
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button onClick={copyDescAndGL} className="btn btn-secondary" style={{ fontSize: '0.85rem' }}>
                Copy Description + GL
              </button>
              <button onClick={copyGLOnly} className="btn btn-secondary" style={{ fontSize: '0.85rem' }}>
                Copy GL Only
              </button>
            </div>
          )}
        </div>

        {isProcessing && (
          <div style={{ marginBottom: '1rem' }}>
            <div style={{ height: '8px', background: 'var(--color-border)', borderRadius: '4px', overflow: 'hidden' }}>
              <div style={{ width: `${progress}%`, height: '100%', background: 'linear-gradient(90deg, var(--color-primary) 0%, var(--color-primary-dark) 100%)', transition: 'width 0.2s' }}></div>
            </div>
            <div style={{ fontSize: '13px', color: 'var(--color-primary)', marginTop: '0.25rem', textAlign: 'right' }}>{progress}%</div>
          </div>
        )}

        {outputData.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--color-primary)', textAlign: 'left' }}>
                  <th style={{ padding: '0.75rem 0.5rem', color: 'var(--color-primary)' }}>Description</th>
                  <th style={{ padding: '0.75rem 0.5rem', color: 'var(--color-primary)' }}>GL Account</th>
                </tr>
              </thead>
              <tbody>
                {outputData.map((item, index) => (
                  <tr key={index} style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <td style={{ padding: '0.5rem' }}>
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) => handleDescriptionChange(index, e.target.value)}
                        className="form-control"
                        style={{ fontFamily: 'monospace', fontSize: '14px' }}
                      />
                    </td>
                    <td style={{ padding: '0.5rem' }}>
                      {item.needsVerification ? (
                        <div style={{
                          padding: '0.5rem',
                          backgroundColor: '#fffbe6',
                          border: '1px solid #ffe58f',
                          borderRadius: '4px',
                          fontSize: '0.85rem'
                        }}>
                          Suggestion: <strong>{item.newlyCalculatedAccount}</strong>
                          <div style={{ marginTop: '0.5rem' }}>
                            <button onClick={() => handleVerification(index, false)} className="btn btn-secondary" style={{ marginRight: '0.5rem', padding: '0.25rem 0.5rem', fontSize: '12px' }}>Keep Current</button>
                            <button onClick={() => handleVerification(index, true)} className="btn btn-primary" style={{ padding: '0.25rem 0.5rem', fontSize: '12px' }}>Use New</button>
                          </div>
                        </div>
                      ) : (
                        <span style={{ fontFamily: 'monospace', fontSize: '14px' }}>
                          {item.glAccount}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p style={{ color: 'var(--color-text-muted)', textAlign: 'center', padding: '2rem', fontStyle: 'italic' }}>
            Paste descriptions above to see results here
          </p>
        )}
      </div>
    </div>
  );
}

export default Classifier;